/**
 * Schrijfvlak van een notitie-pagina (Notion-achtig, 25 sep 2026).
 *
 * Zelfde TipTap + markdown-opslag als ui-components/NotitieEditor (die blijft voor het
 * projectpaneel), maar zonder vaste werkbalk: blokken kies je met "/", opmaak verschijnt
 * pas bij een tekstselectie, en ☐ in een lijst is een aanklikbaar vinkje.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, Paper, Popper, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { Markdown } from 'tiptap-markdown';
import { absoluteAfbeeldingsUrl, afbeeldingenUit, uploadNotitieAfbeelding } from '../../utils/notitieAfbeeldingen';
import { filterSlash, slashQuery, VINKJE_OPEN, VINKJE_DICHT, wisselVinkje } from './notitieLogica';

const INKT = '#1f2937';
const STIL = '#94a3b8';

const NotitieAfbeelding = Image.extend({
  renderHTML({ HTMLAttributes }) {
    return ['img', { ...HTMLAttributes, src: absoluteAfbeeldingsUrl(HTMLAttributes.src),
      style: 'max-width:100%;height:auto;border-radius:6px;display:block;margin:8px 0' }];
  },
});

async function voegAfbeeldingenIn(editor, files) {
  for (const file of files) {
    const id = toast.loading(`Afbeelding uploaden… (${Math.round(file.size / 1024)} kB)`);
    try {
      const url = await uploadNotitieAfbeelding(file); // eslint-disable-line no-await-in-loop
      editor.chain().focus().setImage({ src: url, alt: file.name || '' }).run();
      toast.success('Afbeelding toegevoegd', { id });
    } catch (e) { toast.error(e.message || 'Afbeelding uploaden mislukt', { id }); }
  }
}

function SelectieKnop({ actief, onClick, children, titel }) {
  return (
    <ButtonBase title={titel} onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      sx={{ px: 1, py: 0.5, borderRadius: 1, fontSize: 13, fontWeight: 700, color: actief ? '#fff' : '#e5e7eb',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}>
      {children}
    </ButtonBase>
  );
}

/**
 * @param onSlashActie  (key) => void voor acties die de pagina afhandelt (meeting, sjabloon:*).
 * @param focusRef      krijgt { focusBegin() } zodat de titel met Enter naar de tekst kan.
 */
export default function NotitieSchrijver({ value = '', onChange, placeholder, onSlashActie, focusRef }) {
  const bestandRef = useRef(null);
  const editorRef = useRef(null);
  const [slash, setSlash] = useState(null);      // { van, query, rect }
  const [index, setIndex] = useState(0);
  const slashRef = useRef(null);
  const items = useMemo(() => (slash ? filterSlash(slash.query) : []), [slash]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const indexRef = useRef(0);
  indexRef.current = index;

  const bekijkSlash = (ed) => {
    const { state, view } = ed;
    const { selection } = state;
    if (!selection.empty) { slashRef.current = null; setSlash(null); return; }
    const { $from } = selection;
    const voor = $from.parent.textBetween(0, $from.parentOffset, undefined, '￼');
    const q = slashQuery(voor);
    if (q === null) { slashRef.current = null; setSlash(null); return; }
    const c = view.coordsAtPos($from.pos);
    const nieuw = { van: $from.pos - q.length - 1, tot: $from.pos, query: q, rect: { top: c.top, bottom: c.bottom, left: c.left } };
    if (!slashRef.current || slashRef.current.query !== q) setIndex(0);
    slashRef.current = nieuw;
    setSlash(nieuw);
  };

  const voerUit = (item) => {
    const ed = editorRef.current;
    const s = slashRef.current;
    if (!ed || !s || !item) return;
    slashRef.current = null;
    setSlash(null);
    const keten = ed.chain().focus().deleteRange({ from: s.van, to: s.tot });
    switch (item.key) {
      case 'tekst': keten.setParagraph().run(); break;
      case 'kop': keten.setHeading({ level: 2 }).run(); break;
      case 'subkop': keten.setHeading({ level: 3 }).run(); break;
      case 'lijst': keten.toggleBulletList().run(); break;
      case 'genummerd': keten.toggleOrderedList().run(); break;
      case 'todo': keten.toggleBulletList().insertContent(`${VINKJE_OPEN} `).run(); break;
      case 'citaat': keten.toggleBlockquote().run(); break;
      case 'lijn': keten.setHorizontalRule().run(); break;
      case 'afbeelding': keten.run(); bestandRef.current?.click(); break;
      default: keten.run(); onSlashActie?.(item.key, ed); break;
    }
  };
  const voerUitRef = useRef(voerUit);
  voerUitRef.current = voerUit;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder: placeholder || 'Typ iets, of "/" voor een kop, lijst, to-do of sjabloon…' }),
      NotitieAfbeelding.configure({ inline: false, allowBase64: false }),
      Markdown.configure({ html: false, linkify: true, breaks: true }),
    ],
    content: value,
    editorProps: {
      handlePaste: (view, event) => {
        const files = afbeeldingenUit(event.clipboardData);
        if (!files.length) return false;
        event.preventDefault();
        voegAfbeeldingenIn(editorRef.current, files);
        return true;
      },
      handleDrop: (view, event) => {
        const files = afbeeldingenUit(event.dataTransfer);
        if (!files.length) return false;
        event.preventDefault();
        voegAfbeeldingenIn(editorRef.current, files);
        return true;
      },
      handleKeyDown: (view, event) => {
        if (!slashRef.current) return false;
        const lijst = itemsRef.current;
        if (event.key === 'Escape') { slashRef.current = null; setSlash(null); return true; }
        if (!lijst.length) return false;
        if (event.key === 'ArrowDown') { setIndex((i) => (i + 1) % lijst.length); return true; }
        if (event.key === 'ArrowUp') { setIndex((i) => (i - 1 + lijst.length) % lijst.length); return true; }
        if (event.key === 'Enter' || event.key === 'Tab') { voerUitRef.current(lijst[Math.min(indexRef.current, lijst.length - 1)]); return true; }
        return false;
      },
      // ☐ / ☑ aanklikken = afvinken.
      handleClick: (view, pos) => {
        const { doc } = view.state;
        for (const p of [pos, pos - 1]) {
          if (p < 0 || p >= doc.content.size) continue;
          const teken = doc.textBetween(p, p + 1);
          if (teken === VINKJE_OPEN || teken === VINKJE_DICHT) {
            view.dispatch(view.state.tr.insertText(wisselVinkje(teken), p, p + 1));
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.storage.markdown.getMarkdown());
      bekijkSlash(ed);
    },
    onSelectionUpdate: ({ editor: ed }) => bekijkSlash(ed),
    onBlur: () => { setTimeout(() => { slashRef.current = null; setSlash(null); }, 150); },
  });
  editorRef.current = editor;

  useEffect(() => {
    if (!focusRef) return;
    focusRef.current = { focusBegin: () => editorRef.current?.chain().focus('start').run() };
  }, [focusRef]);

  // Extern veranderd (sjabloon/meeting via de pagina, andere notitie geopend): overnemen,
  // maar nooit tijdens het typen.
  useEffect(() => {
    if (!editor) return;
    const huidige = editor.storage.markdown.getMarkdown();
    if (value === huidige) return;
    if (value === '' && huidige !== '') { editor.commands.clearContent(); return; }
    if (!editor.isFocused) editor.commands.setContent(value, false);
  }, [value, editor]);

  const anker = useMemo(() => (slash ? {
    getBoundingClientRect: () => ({ top: slash.rect.bottom, bottom: slash.rect.bottom, left: slash.rect.left, right: slash.rect.left, width: 0, height: 0, x: slash.rect.left, y: slash.rect.bottom }),
  } : null), [slash]);

  if (!editor) return null;

  return (
    <Box sx={{
      minHeight: 280, cursor: 'text', color: INKT,
      '& .ProseMirror': { outline: 'none', minHeight: 260, fontSize: 15.5, lineHeight: 1.7 },
      '& .ProseMirror p': { m: 0, mb: 0.75 },
      '& .ProseMirror h1': { fontSize: 22, fontWeight: 700, m: 0, mt: 2.5, mb: 0.75 },
      '& .ProseMirror h2': { fontSize: 19, fontWeight: 700, m: 0, mt: 2.25, mb: 0.75 },
      '& .ProseMirror h3': { fontSize: 16.5, fontWeight: 700, m: 0, mt: 1.75, mb: 0.5 },
      '& .ProseMirror ul, & .ProseMirror ol': { m: 0, mb: 0.75, pl: 3 },
      '& .ProseMirror li p': { mb: 0.25 },
      '& .ProseMirror blockquote': { borderLeft: '3px solid #e5e7eb', pl: 1.75, ml: 0, color: '#4b5563' },
      '& .ProseMirror hr': { border: 0, borderTop: '1px solid #e5e7eb', my: 2 },
      '& .ProseMirror a': { color: INKT, textDecorationColor: '#cbd5e1' },
      '& .ProseMirror img.ProseMirror-selectednode': { outline: '2px solid #526075' },
      '& .ProseMirror p.is-editor-empty:first-of-type::before': {
        content: 'attr(data-placeholder)', color: '#b6bec9', float: 'left', height: 0, pointerEvents: 'none',
      },
    }} onClick={(e) => { if (e.target === e.currentTarget) editor.chain().focus('end').run(); }}>
      <BubbleMenu editor={editor} tippyOptions={{ duration: 120 }}>
        <Box sx={{ display: 'flex', gap: 0.25, bgcolor: '#1f2937', borderRadius: 1.5, px: 0.5, py: 0.25, boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>
          <SelectieKnop titel="Vet (⌘B)" actief={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>B</SelectieKnop>
          <SelectieKnop titel="Cursief (⌘I)" actief={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><i>i</i></SelectieKnop>
          <SelectieKnop titel="Doorhalen" actief={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></SelectieKnop>
          <SelectieKnop titel="Kop" actief={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H</SelectieKnop>
          <SelectieKnop titel="Citaat" actief={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>“</SelectieKnop>
        </Box>
      </BubbleMenu>
      <EditorContent editor={editor} />
      <input ref={bestandRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden
        onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) voegAfbeeldingenIn(editor, files); }} />
      <Popper open={!!slash && items.length > 0} anchorEl={anker} placement="bottom-start" sx={{ zIndex: 1400 }}>
        <Paper elevation={0} onMouseDown={(e) => e.preventDefault()}
          sx={{ mt: 0.5, width: 260, maxHeight: 320, overflowY: 'auto', py: 0.5, borderRadius: 2, border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' }}>
          {items.map((it, i) => (
            <ButtonBase key={it.key} onClick={() => voerUit(it)} onMouseEnter={() => setIndex(i)}
              sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', px: 1.5, py: 0.75,
                bgcolor: i === index ? '#f3f4f6' : 'transparent' }}>
              <Typography sx={{ fontSize: 13.5, color: INKT }}>{it.label}</Typography>
              <Typography sx={{ fontSize: 12, color: STIL }}>{it.hint}</Typography>
            </ButtonBase>
          ))}
        </Paper>
      </Popper>
    </Box>
  );
}
